import { useItem } from '@/composables/item'
import { usersService } from '@/services/api'
import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref } from 'vue'
import { useAlertStore, useUserStore } from '../system'
import { usePropertyManagerStore } from './propertyManager'
import { clearProfileImageCache } from '@/composables/useProfileImage'

type Image = { id: number; data: ArrayBuffer }

export const useUsersStore = defineStore('users', () => {
	const {
		items,
		show: itemShow,
		create,
		update,
	} = useItem<Api.User>(usersService, 'users', 'User', {
		options: { postOnPut: true },
	})

	const images = ref<Image[]>([])
	const status = ref<Api.Status>('initial')
	const inflight = new Map<number, Promise<ArrayBuffer>>()
	const showInflight = new Map<string, ReturnType<typeof itemShow>>()

	// Gleichzeitige show()-Aufrufe für dieselbe ID teilen sich einen Request (Muster loadImage)
	function show(
		args: Parameters<typeof itemShow>[0]
	): ReturnType<typeof itemShow> {
		const key = `${args.id}:${args.force ? 1 : 0}`
		const existing = showInflight.get(key)
		if (existing) {
			return existing
		}
		const promise = itemShow(args).finally(() => {
			showInflight.delete(key)
		})
		showInflight.set(key, promise)
		return promise
	}

	function statusSet(value) {
		status.value = value
	}
	function saveProfileImage(data: Image) {
		const idx = images.value.findIndex((item) => item.id === data.id)
		if (idx >= 0) {
			images.value[idx] = data
		} else {
			images.value.push(data)
		}
	}
	function removeImage(id) {
		clearProfileImageCache(id)
		const index = images.value.findIndex((item) => item.id === id)
		if (index >= 0) {
			images.value.splice(index, 1)
		}
	}
	function getProfileImage(id: number) {
		return images.value.find((x) => x.id === parseInt(id))
	}

	function attachClient({ account, client }) {
		statusSet('loading')
		return usersService.attachToDatabase({ account, client }).then(
			(status) => {
				statusSet('ready')
				return status
			},
			(error) => {
				statusSet('error')
				useAlertStore().errorAlert(error)
				return error
			}
		)
	}
	function loadImage(id: number): Promise<ArrayBuffer> {
		statusSet('loading')
		const cached = getProfileImage(id)
		if (cached) {
			return Promise.resolve(cached.data)
		}
		const existing = inflight.get(id)
		if (existing) {
			return existing
		}
		const promise = usersService
			.itemImageGet(id)
			.then(
				({ data }) => {
					if (data.byteLength === 0) throw Error
					statusSet('ready')
					saveProfileImage({ id, data })
					return data
				},
				(error) => {
					// Ein fehlendes Profilbild ist kein Fehler für den Nutzer: kein Dialog, kein
					// Fehlerstatus im Store. Die Aufrufer (useProfileImage) zeigen den Platzhalter.
					statusSet('ready')
					throw error
				}
			)
			.finally(() => {
				inflight.delete(id)
			})
		inflight.set(id, promise)
		return promise
	}
	function updateClientAccount({ account, client, data }) {
		statusSet('loading')
		return usersService.accountDatabasePut({ account, client, data }).then(
			({ data }) => {
				statusSet('ready')
				return data
			},
			(error) => {
				statusSet('error')
				useAlertStore().errorAlert(error)
				return error
			}
		)
	}
	function uploadImage({ id, data }) {
		statusSet('loading')
		return usersService
			.itemImagePost({
				id,
				data: {
					data,
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				},
			})
			.then(
				({ data }) => {
					// Altes Bild nicht weiter aus Store/Profilbild-Cache ausliefern
					removeImage(id)
					return useUserStore()
						.updateWhoami()
						.then(() => {
							statusSet('ready')
							return data
						})
				},
				(error) => {
					statusSet('error')
					useAlertStore().errorAlert(error)
					return error
				}
			)
			.then(() => {
				usePropertyManagerStore().itemRemove(id)
			})
	}
	function search({ value, field = 'email' }) {
		return usersService.search(value, field).then(({ data }) => {
			if (data?.length) {
				data[0]
			}

			return undefined
		})
	}

	return {
		items,
		show,
		create,
		update,

		attachClient,
		loadImage,
		updateClientAccount,

		statusSet,
		saveProfileImage,
		removeImage,
		getProfileImage,
		uploadImage,
		search,
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useUsersStore, import.meta.hot))
}
